import React, { useState, useContext, useRef, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { AuthContext } from "../context/AuthContext";

const MedimagicIcon = ({ size = 52 }) => (
  <svg width={size} height={size} viewBox="0 0 80 80" fill="none">
    <path d="M40 6 L74 40 L40 74 L6 40 Z" stroke="#3072FF" strokeWidth="5.5" fill="none" strokeLinejoin="round" />
    <polyline points="12,40 25,40 31,20 40,60 49,40 62,40 68,40" stroke="#43E0FF" strokeWidth="3.5" fill="none" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const Petel = ({ style, color, size = 17 }) => (
  <span className="petel-dot" style={{ width: size, height: size, borderRadius: "50%", background: color, position: "absolute", ...style }} />
);

// ── 4-digit OTP input ─────────────────────────────────────────────────────────
const OTP_LENGTH = 4;

function OTPInput({ value, onChange }) {
  const refs = useRef([]);
  const digits = (value + " ".repeat(OTP_LENGTH)).slice(0, OTP_LENGTH).split("");

  const handleChange = (i, e) => {
    const v = e.target.value.replace(/\D/g, "").slice(-1);
    const arr = digits.map((d) => d.trim());
    arr[i] = v;
    onChange(arr.join("").trim());
    if (v && i < OTP_LENGTH - 1) refs.current[i + 1]?.focus();
  };

  const handleKeyDown = (i, e) => {
    if (e.key === "Backspace") {
      if (!digits[i].trim() && i > 0) {
        const arr = digits.map((d) => d.trim());
        arr[i - 1] = "";
        onChange(arr.join("").trim());
        refs.current[i - 1]?.focus();
      }
    }
  };

  const handlePaste = (e) => {
    const pasted = e.clipboardData.getData("text").replace(/\D/g, "").slice(0, OTP_LENGTH);
    onChange(pasted);
    refs.current[Math.min(pasted.length, OTP_LENGTH - 1)]?.focus();
    e.preventDefault();
  };

  return (
    <div className="otp-inputs">
      {Array.from({ length: OTP_LENGTH }, (_, i) => i).map((i) => (
        <input
          key={i}
          ref={(el) => (refs.current[i] = el)}
          className={`otp-box${digits[i].trim() ? " filled" : ""}`}
          type="text"
          inputMode="numeric"
          maxLength={1}
          value={digits[i].trim()}
          onChange={(e) => handleChange(i, e)}
          onKeyDown={(e) => handleKeyDown(i, e)}
          onPaste={handlePaste}
          data-testid={`otp-box-${i}`}
        />
      ))}
    </div>
  );
}

// ── Timer ─────────────────────────────────────────────────────────────────────
function ResendTimer({ onResend }) {
  const [seconds, setSeconds] = useState(60);
  useEffect(() => {
    if (seconds <= 0) return;
    const t = setTimeout(() => setSeconds((s) => s - 1), 1000);
    return () => clearTimeout(t);
  }, [seconds]);

  if (seconds > 0) return <p className="otp-timer">Resend OTP in <strong>{seconds}s</strong></p>;
  return (
    <p className="otp-timer">
      Didn't receive it?{" "}
      <button className="login-link" onClick={() => { setSeconds(60); onResend(); }} data-testid="resend-otp-btn">
        Resend OTP
      </button>
    </p>
  );
}

// ── Main Page ─────────────────────────────────────────────────────────────────
export default function UserLoginPage() {
  const { requestOTP, verifyOTP, user } = useContext(AuthContext);
  const navigate = useNavigate();

  // Redirect if already logged in
  useEffect(() => {
    if (user) {
      navigate(user.role === "admin" ? "/dashboard" : "/users/dashboard", { replace: true });
    }
  }, [user, navigate]);

  const [step, setStep] = useState("email"); // "email" | "otp"
  const [email, setEmail] = useState("");
  const [otp, setOtp] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const handleSendOTP = async (e) => {
    e?.preventDefault();
    if (!email.trim()) { setError("Please enter your email."); return; }
    setLoading(true); setError("");
    const result = await requestOTP(email.trim());
    setLoading(false);
    if (result.success) {
      setStep("otp");
    } else {
      setError(result.error);
    }
  };

  const handleVerify = async (e) => {
    e?.preventDefault();
    if (otp.length < OTP_LENGTH) { setError(`Please enter the complete ${OTP_LENGTH}-digit OTP.`); return; }
    setLoading(true); setError("");
    const result = await verifyOTP(email.trim(), otp);
    setLoading(false);
    if (result.success) {
      navigate("/users/dashboard");
    } else {
      setError(result.error);
    }
  };

  return (
    <div className="login-page-root">
      {/* Left panel */}
      <div className="login-left-panel">
        <Petel style={{ top: 80,  right: 60  }} color="#43E0FF" size={35} />
        <Petel style={{ top: 200, left: 40   }} color="#9070FE" size={17} />
        <Petel style={{ top: 340, right: 40  }} color="#FE4A9E" size={17} />
        <Petel style={{ bottom: 200, left: 80 }} color="#43E0FF" size={22} />
        <Petel style={{ bottom: 100, right: 80 }} color="#9070FE" size={17} />
        <Petel style={{ bottom: 300, left: 160 }} color="#FE4A9E" size={14} />

        <div className="login-logo">
          <MedimagicIcon size={52} />
          <div className="login-logo-text">
            <span className="logo-name">medimagic</span>
            <span className="logo-sub">dashboard</span>
          </div>
        </div>

        <div className="login-brand-heading">
          <p className="login-brand-label">College & IT Portal</p>
          <h1 className="login-brand-h1">
            <span className="login-brand-line">Secure access</span>
            <span className="login-brand-line">for college</span>
            <span className="login-brand-line highlight-line">admins</span>
          </h1>
          <p className="login-brand-sub">
            Login with your registered email. A one-time password will be sent for verification.
          </p>
        </div>

        <div className="login-stats">
          <div className="login-stat"><span className="login-stat-num">OTP</span><span className="login-stat-label">Based Login</span></div>
          <div className="login-stat-divider" />
          <div className="login-stat"><span className="login-stat-num">10m</span><span className="login-stat-label">OTP Validity</span></div>
          <div className="login-stat-divider" />
          <div className="login-stat"><span className="login-stat-num">TLS</span><span className="login-stat-label">Encrypted</span></div>
        </div>
      </div>

      {/* Right panel */}
      <div className="login-right-panel">
        <div className="login-form-wrap">

          {step === "email" ? (
            <>
              <div className="login-form-header">
                <h2 className="login-form-title">Sign In.</h2>
                <p className="login-form-sub">Enter your registered email to receive an OTP.</p>
              </div>
              {error && <div className="login-error" data-testid="login-error">{error}</div>}
              <form onSubmit={handleSendOTP} className="login-form">
                <div className="login-field">
                  <label className="login-label">Email Address</label>
                  <input
                    type="email"
                    className="login-input"
                    placeholder="you@college.edu"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    data-testid="email-input"
                    autoFocus
                  />
                </div>
                <button type="submit" className="login-btn" disabled={loading} data-testid="send-otp-btn">
                  {loading ? <span className="login-spinner" /> : "Send OTP"}
                </button>
              </form>
              <p className="login-toggle-text">
                Super Admin?{" "}
                <Link to="/login" className="login-link">Sign in here</Link>
              </p>
            </>
          ) : (
            <>
              <div className="login-form-header">
                <h2 className="login-form-title">Verify OTP.</h2>
                <p className="login-form-sub">
                  A {OTP_LENGTH}-digit OTP has been sent to <strong>{email}</strong>.
                </p>
              </div>
              {error && <div className="login-error" data-testid="otp-error">{error}</div>}
              <form onSubmit={handleVerify} className="login-form">
                <div className="login-field">
                  <label className="login-label">One-Time Password</label>
                  <OTPInput value={otp} onChange={setOtp} />
                </div>
                <button type="submit" className="login-btn" disabled={loading || otp.length < OTP_LENGTH} data-testid="verify-otp-btn">
                  {loading ? <span className="login-spinner" /> : "Verify & Sign In"}
                </button>
              </form>
              <ResendTimer onResend={() => { setOtp(""); setError(""); handleSendOTP(); }} />
              <p className="login-toggle-text">
                <button className="login-link" onClick={() => { setStep("email"); setOtp(""); setError(""); }}>
                  Change email
                </button>
              </p>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
